import { useState } from "react";
import { formatDate } from "@/lib/format";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { PlatformPageHeader } from "@/components/PlatformLayout";
import {
  usePlatformOrgs,
  type PlatformOrgsParams,
} from "@/lib/platform";

const KYC_FILTERS: { value: string; labelKey: string }[] = [
  { value: "", labelKey: "platform.orgs.filter.any" },
  { value: "unverified", labelKey: "platform.orgs.filter.unverified" },
  { value: "pending", labelKey: "platform.orgs.filter.pending" },
  { value: "verified", labelKey: "platform.orgs.filter.verified" },
  { value: "rejected", labelKey: "platform.orgs.filter.rejected" },
];

const STATUS_FILTERS: { value: string; labelKey: string }[] = [
  { value: "", labelKey: "platform.orgs.filter.any" },
  { value: "active", labelKey: "platform.orgs.filter.active" },
  { value: "suspended", labelKey: "platform.orgs.filter.suspended" },
];

export default function PlatformOrgListPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useSearchParams();
  const [cursor, setCursor] = useState<string>("");
  const [history, setHistory] = useState<string[]>([]);

  const params: PlatformOrgsParams = {
    kyc: (search.get("kyc") || undefined) as PlatformOrgsParams["kyc"],
    status: (search.get("status") || undefined) as PlatformOrgsParams["status"],
    flagged: search.get("flagged") === "1",
    sort: "-created_at",
    limit: 25,
    cursor: cursor || undefined,
  };
  const list = usePlatformOrgs(params);

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(search);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearch(next, { replace: true });
    setCursor("");
    setHistory([]);
  }

  return (
    <div className="space-y-4">
      <PlatformPageHeader
        title={t("platform.nav.orgs")}
        description={t("platform.orgs.desc")}
        action={
          <div className="text-xs text-nb-mute">
            {list.data ? t("platform.orgs.shown", { count: list.data.results.length }) : ""}
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3 rounded-md border border-nb-border bg-nb-surface p-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-nb-mute">{t("platform.orgs.kyb")}</span>
          <select
            value={search.get("kyc") || ""}
            onChange={(e) => setFilter("kyc", e.target.value)}
            className="rounded-md border border-nb-border bg-nb-bg px-2 py-1"
            data-testid="kyc-filter"
          >
            {KYC_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {t(f.labelKey)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-nb-mute">{t("platform.common.status")}</span>
          <select
            value={search.get("status") || ""}
            onChange={(e) => setFilter("status", e.target.value)}
            className="rounded-md border border-nb-border bg-nb-bg px-2 py-1"
            data-testid="status-filter"
          >
            {STATUS_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {t(f.labelKey)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={search.get("flagged") === "1"}
            onChange={(e) =>
              setFilter("flagged", e.target.checked ? "1" : "")
            }
          />
          <span>{t("platform.orgs.flaggedOnly")}</span>
        </label>
      </div>

      {list.isLoading && <div className="text-sm text-nb-mute">{t("common.loading")}</div>}
      {list.isError && (
        <div className="text-sm text-nb-danger">
          {t("platform.orgs.loadError")}
        </div>
      )}

      {list.data && (
        <table className="w-full overflow-hidden rounded-md border border-nb-border text-sm">
          <thead className="bg-nb-surface text-left text-xs uppercase tracking-wide text-nb-mute">
            <tr>
              <th className="px-3 py-2">{t("platform.orgs.col.name")}</th>
              <th className="px-3 py-2">{t("platform.orgs.col.country")}</th>
              <th className="px-3 py-2">{t("platform.orgs.col.kyb")}</th>
              <th className="px-3 py-2">{t("platform.orgs.col.sanctions")}</th>
              <th className="px-3 py-2">{t("platform.orgs.col.created")}</th>
            </tr>
          </thead>
          <tbody>
            {list.data.results.map((row) => (
              <tr
                key={row.id}
                className="border-t border-nb-border hover:bg-nb-surface/60"
              >
                <td className="px-3 py-2">
                  <Link
                    to={`/platform/orgs/${row.id}`}
                    className="text-nb-accent hover:underline"
                    data-testid={`org-link-${row.id}`}
                  >
                    {row.name}
                  </Link>
                  {row.legal_name && (
                    <div className="text-xs text-nb-mute">
                      {row.legal_name}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-nb-mute">{row.country || "—"}</td>
                <td className="px-3 py-2">
                  <KycBadge status={row.kyc_status} />
                </td>
                <td className="px-3 py-2">
                  {row.sanctions_match_found ? (
                    <span className="rounded bg-nb-danger/20 px-2 py-0.5 text-xs text-nb-danger">
                      {t("platform.orgs.flagged")}
                    </span>
                  ) : (
                    <span className="text-xs text-nb-mute">{t("platform.orgs.clean")}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-nb-mute">
                  {row.created_at
                    ? formatDate(row.created_at)
                    : "—"}
                </td>
              </tr>
            ))}
            {list.data.results.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-6 text-center text-sm text-nb-mute"
                >
                  {t("platform.orgs.noMatch")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <div className="flex justify-between text-sm">
        <button
          type="button"
          className="btn-ghost"
          disabled={history.length === 0}
          onClick={() => {
            const next = [...history];
            next.pop();
            setHistory(next);
            setCursor(next[next.length - 1] ?? "");
          }}
        >
          {t("platform.common.prev")}
        </button>
        <button
          type="button"
          className="btn-ghost"
          disabled={!list.data?.has_more}
          onClick={() => {
            if (!list.data?.next_cursor) return;
            setHistory([...history, list.data.next_cursor]);
            setCursor(list.data.next_cursor);
          }}
        >
          {t("platform.common.next")}
        </button>
      </div>
    </div>
  );
}

function KycBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const tone =
    status === "verified"
      ? "bg-nb-ok/20 text-nb-ok"
      : status === "rejected"
        ? "bg-nb-danger/20 text-nb-danger"
        : status === "pending"
          ? "bg-nb-warn/20 text-nb-warn"
          : "bg-nb-surface text-nb-mute";
  return (
    <span className={`rounded px-2 py-0.5 text-xs ${tone}`}>
      {t(`platform.orgs.filter.${status}`, status)}
    </span>
  );
}
